import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { DownOutlined } from "@ant-design/icons";
import { CopyShortUrlButton } from "@/shared/components/Button/CopyShortUrlButton";
import { TAP_TARGET } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";

/*
 * Phone view of VideoTable: one accordion per creator. Collapsed rows show the creator's totals;
 * expanding lists each video with its metrics and its own Copy Link. VideoTable works out every
 * value (order, stars, per-platform rules) and passes it in; this only presents it. Creators load
 * in batches as the list scrolls, instead of the desktop pager.
 */

const BATCH = 10;

export interface PostingMetric {
	label: string;
	value: React.ReactNode;
}

export interface PostingVideo {
	id: string;
	url?: string;
	label: string;
	metrics: PostingMetric[];
}

export interface PostingCreator {
	name: string;
	followers: string;
	totalViews: string;
	star: React.ReactNode;
	videos: PostingVideo[];
}

const List = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-top: 1rem;
`;

const Card = styled.div<{ $active: boolean }>`
	overflow: hidden;
	border: 1px solid ${({ $active }) => ($active ? "#4460ef" : "#e3e6f3")};
	border-radius: 16px;
	background: ${({ $active }) => ($active ? "#ebeefd" : "#ffffff")};
`;

const Header = styled.button`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	min-height: ${TAP_TARGET}px;
	padding: 14px 12px 14px 16px;
	border: none;
	background: transparent;
	font-family: "Poppins", sans-serif;
	text-align: left;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid #3a0ca3;
		outline-offset: -2px;
		border-radius: 16px;
	}

	${pressedTint}
`;

const Identity = styled.span`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

const Name = styled.span`
	display: flex;
	align-items: center;
	gap: 6px;
	min-width: 0;
	color: #222222;
	font-size: 16px;
	font-weight: 600;

	> span:first-child {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`;

const Meta = styled.span`
	overflow: hidden;
	color: #7983b2;
	font-size: 13px;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Total = styled.span`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	flex-shrink: 0;
	color: #222222;
	font-size: 15px;
	font-weight: 600;
	line-height: 20px;

	> small {
		color: #969696;
		font-size: 12px;
		font-weight: 400;
	}
`;

const Chevron = styled(DownOutlined)<{ $open: boolean }>`
	flex-shrink: 0;
	color: #303030;
	font-size: 14px;
	transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
	transition: transform 0.2s;
`;

const VideoBlock = styled.div`
	padding: 14px 16px;
	border-top: 1px solid #eceef6;
	background: #ffffff;
`;

const VideoHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 10px;
`;

const VideoLink = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	min-height: ${TAP_TARGET}px;
	color: #4460ef;
	font-size: 15px;
	font-weight: 500;
	text-decoration: none;
`;

/* Two columns while a label and value pair fits side by side; one column on the narrowest phones. */
const Stats = styled.dl`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
	gap: 8px 16px;
	margin: 0;
`;

const Stat = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 8px;
	min-width: 0;
	font-size: 13px;

	> dt {
		color: #8a8a8a;
		white-space: nowrap;
	}

	> dd {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		margin: 0;
		color: #222222;
		font-weight: 600;
		white-space: nowrap;
	}
`;

const Sentinel = styled.div`
	height: 1px;
`;

interface PostingListProps {
	creators: PostingCreator[];
	activeCreatorId?: string;
	onCreatorClick?: (creatorId: string) => void;
}

export const PostingList: React.FC<PostingListProps> = ({ creators, activeCreatorId, onCreatorClick }) => {
	const [open, setOpen] = useState<Set<string>>(new Set());
	const [shown, setShown] = useState(BATCH);
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setShown(BATCH);
		setOpen(new Set());
	}, [creators]);

	// Load the next batch when the end of the list comes into view.
	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || shown >= creators.length || typeof IntersectionObserver === "undefined") return undefined;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) setShown((count) => count + BATCH);
			},
			{ rootMargin: "400px 0px" },
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [creators.length, shown]);

	const toggle = (name: string) => {
		setOpen((current) => {
			const next = new Set(current);
			if (next.has(name)) next.delete(name);
			else next.add(name);
			return next;
		});
		onCreatorClick?.(name);
	};

	return (
		<List>
			{creators.slice(0, shown).map((creator) => {
				const isOpen = open.has(creator.name);
				const panelId = `posting-${creator.name.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
				const videoCount = `${creator.videos.length} video${creator.videos.length === 1 ? "" : "s"}`;
				return (
					<Card key={creator.name} $active={activeCreatorId === creator.name}>
						<Header
							type="button"
							aria-expanded={isOpen}
							aria-controls={panelId}
							onClick={() => toggle(creator.name)}
						>
							<Identity>
								<Name>
									<span>{creator.name}</span>
									{creator.star}
								</Name>
								<Meta>
									{creator.followers} followers · {videoCount}
								</Meta>
							</Identity>
							<Total>
								{creator.totalViews}
								<small>views</small>
							</Total>
							<Chevron $open={isOpen} aria-hidden />
						</Header>
						{isOpen && (
							<div id={panelId}>
								{creator.videos.map((video) => (
									<VideoBlock key={video.id}>
										<VideoHeader>
											<VideoLink href={video.url} target="_blank" rel="noopener noreferrer">
												{video.label}
											</VideoLink>
											<CopyShortUrlButton targetUrl={video.url} />
										</VideoHeader>
										<Stats>
											{video.metrics.map((metric) => (
												<Stat key={metric.label}>
													<dt>{metric.label}</dt>
													<dd>{metric.value}</dd>
												</Stat>
											))}
										</Stats>
									</VideoBlock>
								))}
							</div>
						)}
					</Card>
				);
			})}
			{shown < creators.length && <Sentinel ref={sentinelRef} aria-hidden />}
		</List>
	);
};
